<script>
import {defineComponent, ref, onBeforeMount, nextTick, computed, watch, reactive} from "vue";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import Column from "primevue/column";
import {useApiCall} from "@/composables/useApiCall";
import {
  getDepartmentsApiCall,
  createDepartmentsApiCall,
  updateDepartmentsApiCall,
  deleteDepartmentsApiCall,
} from "@/services/vacancies";
import {useI18n} from "vue-i18n";
import InputText from "primevue/inputtext";
import Paginator from "primevue/paginator";
import {useStore} from "vuex";
import {required} from "@/utils/i18n-validators";
import {useVuelidate} from "@vuelidate/core";
import Breadcrumb from "@/components/Breadcrumb.vue";

export default defineComponent({
  layout: { name: 'AdminLayout' },
  components: { Breadcrumb, Column, ConfirmationModal, DataTable, Button, InputText, Paginator},
  setup() {
    const { t } = useI18n()
    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const visibleDeleteModal = ref(false)
    const visibleCreateModal = ref(false)
    const deleteId = ref(0)
    const fields = reactive({
      name_ru: '',
      name_en: '',
      name_ch: '',
    })
    const breadcrumbs = ref([]);
    const nameField = computed(() => `name_${lang.value}`);
    const rules = computed(() => ({
      [nameField.value]: {required},
    }));
    const v$ = useVuelidate(rules, fields);
    const currentItem = ref(null)
    const first = ref(0);
    const dataItems = computed(() => itemsData.value?.data)
    const isLoading = ref(false);
    const toggleDeleteModal = (id = 0) => {
      deleteError.value = null
      visibleDeleteModal.value = !visibleDeleteModal.value
      if (id) {
        deleteId.value = id
      } else {
        deleteId.value = 0
      }
    }

    const toggleCreateModal = () => {
      visibleCreateModal.value = !visibleCreateModal.value
      if (!visibleCreateModal.value) {
        fields.name_ru = ''
        fields.name_ch = ''
        fields.name_en = ''
        createError.value = null
        currentItem.value = null
        v$.value.$reset()
      } else if (currentItem.value) {
        fields.name_ru = currentItem.value.name_ru
        fields.name_ch = currentItem.value.name_ch
        fields.name_en = currentItem.value.name_en
      }
    }

    const {
      isLoading: isItemsLoading,
      data: itemsData,
      error: itemsError,
      executeApiCall: getItemsAction,
    } = useApiCall(getDepartmentsApiCall, true)

    const {
      isLoading: isCreateLoading,
      data: createData,
      error: createError,
      executeApiCall: createAction,
    } = useApiCall(createDepartmentsApiCall, true)

    const {
      isLoading: isDeleteLoading,
      data: deleteData,
      error: deleteError,
      executeApiCall: deleteAction,
    } = useApiCall(deleteDepartmentsApiCall, true)

    const {
      isLoading: isUpdateLoading,
      data: updateData,
      error: updateError,
      executeApiCall: updateAction,
    } = useApiCall(updateDepartmentsApiCall, true)

    const loadItems = async () => {
      try {
        isLoading.value = true;
        const filterObject = {
          page: ((first.value / (dataItems.value?.pagination?.per_page ?? 1)) + 1),
        };
        await getItemsAction(filterObject)
        isLoading.value = false;
        window.scrollTo(0, 0);
      } catch {
        console.error(itemsError.value)
      }
    }

    const deleteItem = async () => {
      try {
        await deleteAction({id: deleteId.value})
        if (deleteData.value) {
          toggleDeleteModal(0)
          if (first.value > 1 && dataItems.value.data.length - 1 === 0) {
            first.value--
          }
          await loadItems()
        }
      } catch {
        console.error(deleteError.value)
      }
    }

    const createItem = async () => {
      const result = await v$.value.$validate()
      if (result) {
        try {
          if (!fields.name_ru) {
            fields.name_ru = fields.name_en ? fields.name_en : fields.name_ch
          }
          if(currentItem.value) {
            await updateAction({id: currentItem.value.id, data: fields})
            if (updateData.value) {
              await loadItems()
              toggleEditModal(null)
            }
          } else {
            await createAction(fields)
            if (createData.value) {
              await loadItems()
              toggleCreateModal()
            }
          }
        } catch {
          console.error(createError.value || updateError.value)
        }
      }
    }

    const toggleEditModal = (item) => {
      currentItem.value = item
      toggleCreateModal()
    }

    watch((first), async () => {
      await loadItems()
    });

    onBeforeMount(async () => {
      breadcrumbs.value = [
        {label: 'Сайт / Вакансии', router: {name: 'website', query: {tab: 'vacancies'}}},
        {label: 'Отделы'}
      ];
      await loadItems()
      await nextTick(() => {
        window.scrollTo(0, 0)
      })
    })

    return {
      t,
      deleteItem,
      createItem,
      toggleCreateModal,
      visibleDeleteModal,
      visibleCreateModal,
      deleteId,
      currentItem,
      toggleEditModal,
      toggleDeleteModal,
      nameField,
      fields,
      isCreateLoading,
      isDeleteLoading,
      isUpdateLoading,
      createError,
      updateError,
      isLoading,
      dataItems,
      first,
      v$,
      breadcrumbs,
      deleteError,
    };
  }
});
</script>

<template>
  <ConfirmationModal v-model:visible="visibleDeleteModal">
    <template #header>
      <span>Удалить отдел</span>
    </template>

    <template #default>
      <span class="flex mt-2">Вы уверены, что хотите удалить отдел?</span>
    </template>

    <template #footer>
      <span v-if="deleteError" class="flex text-xs color-red">{{deleteError.error}}</span>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="toggleDeleteModal(0)"
                class="btn-primary-outlined font-light w-12"/>
        <Button :loading="isDeleteLoading" :disabled="isDeleteLoading" :label="t('labels.destroy')" @click="deleteItem"
                class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <ConfirmationModal v-if="visibleCreateModal || currentItem" v-model:visible="visibleCreateModal" closable @close-modal="toggleCreateModal" class="rules__popup">
    <template #header>
      {{ currentItem ? 'Редактирование' : 'Создание' }} отдела
    </template>

    <template #default>
      <div class="vacancies-location__popup-wrapper">
       <span class="p-float-label w-full">
         <InputText
             v-model="fields[nameField]"
             id="name_ru"
             class="w-full"
             :class="{'p-invalid': v$[nameField].$errors?.length || createError?.data?.[nameField] }"

         />
         <label for="name_ru">Введите название отдела</label>
       </span>
        <span v-if="v$[nameField].$errors?.length || createError?.data?.[nameField]" class="vacancies-location__popup-error color-red">
          {{ createError?.data?.[nameField] ? createError?.data[nameField][0] : t(v$[nameField].$errors?.[0].$message) }}
        </span>
      </div>
    </template>

    <template #footer>
      <Button :loading="isCreateLoading" :disabled="isCreateLoading || !fields[nameField]" label="Сохранить" @click="createItem"
              class="btn-primary vacancies-location__header-btn font-light"/>
    </template>
  </ConfirmationModal>

  <section class="vacancies-location__header">
    <Breadcrumb :data="breadcrumbs" separator="/"/>
    <div class="flex gap-3">
      <Button label="Создать отдел" @click="toggleCreateModal" class="btn-primary vacancies-location__header-btn"/>
    </div>
  </section>

  <section class="vacancies-location__body">
    <div class="vacancies-location__body-table">
      <DataTable :value="dataItems?.data ? dataItems.data : []" :loading="isLoading" showGridlines tableStyle="min-width: 50rem" selectionMode="single">
        <Column field="name_ru" header="Название">
          <template #body="slotProps">
            <button @click="toggleEditModal(slotProps.data)" class="vacancies-location__body-table-name">
              <span>{{ slotProps.data[nameField] }}</span>
            </button>
          </template>
        </Column>
        <Column field="text" header="Действия" style="width: 150px">
          <template #body="slotProps">
            <div v-if="slotProps.data.id > 0" class="vacancies-location__body-table-actions">
              <button @click="toggleEditModal(slotProps.data)" class="vacancies-location__body-table-edit">
                <img src="@/assets/images/icons/Edit.svg" width="24" alt="edit-icon"/>
              </button>
              <button  @click="toggleDeleteModal(slotProps.data.id)" class="vacancies-location__body-table-delete">
                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.2679 5.36049H14.7322C14.7322 4.76849 14.497 4.20074 14.0784 3.78213C13.6598 3.36352 13.092 3.12835 12.5 3.12835C11.908 3.12835 11.3403 3.36352 10.9217 3.78213C10.503 4.20074 10.2679 4.76849 10.2679 5.36049ZM8.92859 5.36049C8.92859 4.41329 9.30486 3.50488 9.97464 2.83511C10.6444 2.16534 11.5528 1.78906 12.5 1.78906C13.4472 1.78906 14.3556 2.16534 15.0254 2.83511C15.6952 3.50488 16.0714 4.41329 16.0714 5.36049H21.6518C21.8294 5.36049 21.9997 5.43104 22.1253 5.55662C22.2509 5.68221 22.3214 5.85253 22.3214 6.03013C22.3214 6.20773 22.2509 6.37806 22.1253 6.50364C21.9997 6.62923 21.8294 6.69978 21.6518 6.69978H20.4822L19.3956 19.7382C19.3165 20.6867 18.8839 21.5708 18.1835 22.2153C17.4831 22.8598 16.5661 23.2176 15.6143 23.2176H9.38573C8.43394 23.2176 7.51693 22.8598 6.81654 22.2153C6.11615 21.5708 5.68354 20.6867 5.60448 19.7382L4.51787 6.69978H3.34823C3.17063 6.69978 3.00031 6.62923 2.87472 6.50364C2.74914 6.37806 2.67859 6.20773 2.67859 6.03013C2.67859 5.85253 2.74914 5.68221 2.87472 5.55662C3.00031 5.43104 3.17063 5.36049 3.34823 5.36049H8.92859ZM6.9393 19.6266C6.99037 20.2403 7.27022 20.8125 7.72336 21.2296C8.17651 21.6467 8.76985 21.8782 9.38573 21.8783H15.6143C16.2302 21.8782 16.8235 21.6467 17.2767 21.2296C17.7298 20.8125 18.0097 20.2403 18.0607 19.6266L19.1393 6.69978H5.86162L6.9393 19.6266ZM10.4911 9.82478C10.6687 9.82478 10.839 9.89533 10.9646 10.0209C11.0902 10.1465 11.1607 10.3168 11.1607 10.4944V18.0837C11.1607 18.2613 11.0902 18.4316 10.9646 18.5572C10.839 18.6828 10.6687 18.7533 10.4911 18.7533C10.3135 18.7533 10.1432 18.6828 10.0176 18.5572C9.892 18.4316 9.82145 18.2613 9.82145 18.0837V10.4944C9.82145 10.3168 9.892 10.1465 10.0176 10.0209C10.1432 9.89533 10.3135 9.82478 10.4911 9.82478ZM15.1786 10.4944C15.1786 10.3168 15.108 10.1465 14.9825 10.0209C14.8569 9.89533 14.6865 9.82478 14.5089 9.82478C14.3313 9.82478 14.161 9.89533 14.0354 10.0209C13.9099 10.1465 13.8393 10.3168 13.8393 10.4944V18.0837C13.8393 18.2613 13.9099 18.4316 14.0354 18.5572C14.161 18.6828 14.3313 18.7533 14.5089 18.7533C14.6865 18.7533 14.8569 18.6828 14.9825 18.5572C15.108 18.4316 15.1786 18.2613 15.1786 18.0837V10.4944Z" fill="#E26B6B"/>
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
      <Paginator
          v-model:first="first"
          :rows="dataItems?.pagination?.per_page ?? 0"
          :totalRecords="dataItems?.pagination?.total ?? 0"
          class="justify-content-start"
      ></Paginator>
    </div>
  </section>
</template>

<style lang="scss">
.vacancies-location__header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
  padding-top: 14px;
  margin-bottom: 24px;
}
.vacancies-location__header-btn {
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  min-width: 228px;
  height: 46px;

  color: white;
  background: #C3987A;
}
.vacancies-location__body-table {
  .p-column-header-content  {
    justify-content: start !important;
    .p-column-title {
      font-weight: 600;
      font-size: 18px;
      line-height: 100%;
      text-align: start;
      color: #333333;
    }
  }
}

.vacancies-location__body-table-name,
.vacancies-location__body-table-permissions {
  font-weight: 400;
  font-size: 18px;
  line-height: 100%;
  text-align: left;
  color: #333333;
}
.vacancies-location__body-table-name {
  display: flex;
  justify-content: start;
  text-decoration: underline;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 4px;
  span::first-letter {
    text-transform: uppercase;
  }
}
.vacancies-location__body-table-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}
.vacancies-location__body-table-actions:before {
  position: absolute;
  content: '';
  width: 1px;
  height: 25px;
  background: #E1E8ED;
  top: 50%;
  left: calc(50% - 0.5px);
  transform: translateY(-50%);
  z-index: 2;
}
.vacancies-location__body-table-delete,
.vacancies-location__body-table-edit {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}
.vacancies-location__popup-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 10px;
  width: 470px;
  margin-top: 20px;
}
.vacancies-location__popup-error {
  min-height: 20px;
}
.vacancies-location__popup {
  max-width: unset !important;
  .p-dialog-header-icons {
    position: absolute;
    right: 8px;
    top: 8px;
  }
  .p-dialog-header {
    padding: 20px 20px 16px 20px;
  }
  .p-dialog-footer {
    padding: 0 20px 20px;
    justify-content: start;
    text-align: start !important;
  }
}
</style>